import { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerButtonGroup,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  FormLabel,
} from '@frontend/legacy-components';

import { groupMemberChangeUserGroup } from '../data/actions';
import { getIsChangingUserGroup } from '../data/reducer';
import CurrentGroupCard from './CurrentGroupCard.tsx';
import OptionalGroupCard from './OptionalGroupCard.tsx';
import OptionalGroupsInputFilter from './OptionalGroupsInputFilter.tsx';
import EmptyOptionalGroups from './components/EmptyOptionalGroups';

const fuzzySearchFilter = (groups, input) => {
  return groups.filter((groupData) => {
    const { group } = groupData;
    const { businessName } = group;

    const loweredBusinessName = businessName.toLowerCase();
    const loweredInput = input.toString().toLowerCase();

    if (loweredBusinessName.includes(loweredInput)) return groupData;
  });
};

const SwitchUserGroupDrawer = ({
  onClose,
  isOpen,
  userGroupMembers,
  groupMemberId,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const isChangingGroup = useSelector(getIsChangingUserGroup);
  const currentGroup = userGroupMembers.filter(
    (groupMember) => groupMember.id === groupMemberId,
  )[0];
  const currentGroupId = currentGroup?.id;

  const initialGroupMembersList = useMemo(
    () => userGroupMembers.filter((group) => group.id !== currentGroupId),
    [currentGroupId, userGroupMembers],
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroupMemberId, setSelectedGroupMemberId] = useState(null);
  const [highlightedGroupMemberId, setHighlightedGroupMemberId] =
    useState(null);
  const [groups, setGroups] = useState(initialGroupMembersList);

  const btnRef = useRef();

  /**
   * Handle focus
   */
  useEffect(() => {
    if (selectedGroupMemberId !== null) btnRef.current.focus();
  }, [selectedGroupMemberId]);

  /**
   * Handle search filter
   */
  useEffect(() => {
    if (!searchTerm && groups.length < initialGroupMembersList.length) {
      setGroups(initialGroupMembersList);
      return;
    }

    const debounce = setTimeout(() => {
      const filteredList = fuzzySearchFilter(
        initialGroupMembersList,
        searchTerm,
        'businessName',
      );
      setGroups(filteredList);
    }, 750);

    return () => clearTimeout(debounce);
  }, [initialGroupMembersList, groups.length, searchTerm]);

  const handleChange = (e) => {
    setSearchTerm(e.target.value);
    if (selectedGroupMemberId) setSelectedGroupMemberId(null);
    if (highlightedGroupMemberId) setHighlightedGroupMemberId(null);
  };

  const handleClear = () => setSearchTerm('');

  const handleClose = () => {
    onClose();
    handleClear();
    setHighlightedGroupMemberId(null);
    setSelectedGroupMemberId(null);
  };

  const handleSwitch = async () => {
    await dispatch(
      groupMemberChangeUserGroup.request({
        groupMemberId: selectedGroupMemberId,
        nextUrl: '/home',
      }),
    );
    history.push('/home');
  };

  return (
    <Drawer isOpen={isOpen} onClose={handleClose}>
      <DrawerContent>
        <DrawerHeader>
          Switch organization
          <DrawerCloseButton />
        </DrawerHeader>
        <DrawerBody>
          <Box>
            <FormLabel color="feature.secondary" fontWeight="medium">
              CURRENT ORGANIZATION
            </FormLabel>
            <Box
              border="0.0225rem solid"
              borderColor="feature.placeholder"
              borderRadius="5px"
              paddingTop="0.25rem"
              paddingBottom="0.25rem"
              marginBottom="0.75rem"
            >
              <CurrentGroupCard currentGroup={currentGroup} />
            </Box>
          </Box>

          <Divider />

          <Box paddingTop="0.75rem">
            <FormLabel color="feature.secondary" fontWeight="medium">
              SELECT ORGANIZATION
            </FormLabel>

            <OptionalGroupsInputFilter
              value={searchTerm}
              handleClear={handleClear}
              handleChange={handleChange}
            />

            <Box
              border="0.0225rem solid"
              borderColor="feature.placeholder"
              borderRadius="5px"
              paddingTop="0.25rem"
              paddingBottom="0.25rem"
              marginTop="0.75rem"
              marginBottom="0.75rem"
              display="flex"
              flexDirection="column"
            >
              {groups.length ? (
                groups.map((groupData, idx) => {
                  const { id } = groupData;
                  return (
                    <OptionalGroupCard
                      optionalGroup={groupData}
                      isLast={idx === groups.length - 1}
                      setSelectedGroupMemberId={setSelectedGroupMemberId}
                      selectedGroupMemberId={selectedGroupMemberId}
                      highlightedGroupMemberId={highlightedGroupMemberId}
                      setHighlightedGroupMemberId={setHighlightedGroupMemberId}
                      key={id}
                    />
                  );
                })
              ) : (
                <EmptyOptionalGroups />
              )}
            </Box>
          </Box>
        </DrawerBody>
        <DrawerFooter>
          <DrawerButtonGroup>
            <Button
              isDisabled={isChangingGroup}
              variant="secondary"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              isLoading={isChangingGroup}
              isDisabled={!selectedGroupMemberId ? true : false}
              onClick={handleSwitch}
              ref={btnRef}
            >
              Switch Organization
            </Button>
          </DrawerButtonGroup>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default SwitchUserGroupDrawer;
